class HeroSlideshowSlider extends HTMLElement {
  constructor() {
    super();

    this.track = this.querySelector(".hero-slideshow-slider-track");
    this.slides = this.querySelectorAll(".hero-slideshow-slider-slide");
    this.controls = this.querySelectorAll(".hero-slideshow-slider-control");
    this.paginationDots = this.querySelectorAll(".hero-slideshow-slider-pagination-dots button");
    this.paginationFraction = this.querySelector(".hero-slideshow-slider-pagination-fraction");
    this.controlsPagination = this.querySelector(".hero-slideshow-slider-controls-pagination");
    this.section = this.closest(".hero-slideshow");
    this.hasStaticMobileMediaHeight = this.section?.style.getPropertyValue("--media-height-mobile").trim() !== "";

    const activeIndex = Array.from(this.slides).findIndex((slide) => slide.classList.contains("active"));

    this._currentIndex = activeIndex > -1 ? activeIndex : 0;
    this._activeIndex = this._currentIndex;
    this._autoplayEvents = [];
    this._intervalId = null;

    this._resizeObserver = new ResizeObserver(() => this.update());
    this._onBlockSelect = this.onBlockSelect.bind(this);

    this.handleControls();
    this.handlePagination();
    this.handleKeyboard();
    this.handleTouch();
  }

  connectedCallback() {
    if (!this.track || !this.slides.length) return;

    this._resizeObserver.observe(this.track);
    document.addEventListener("shopify:block:select", this._onBlockSelect);

    this.initAutoplay();
  }

  disconnectedCallback() {
    this._resizeObserver.disconnect();
    document.removeEventListener("shopify:block:select", this._onBlockSelect);

    this.stopAutoplay?.();

    this._autoplayEvents.forEach(([event, handler]) => {
      this.removeEventListener(event, handler);
    });
    this._autoplayEvents = [];
  }

  prev() {
    this._currentIndex = (this._currentIndex - 1 + this.slides.length) % this.slides.length;
    this.update();
  }

  next() {
    this._currentIndex = (this._currentIndex + 1) % this.slides.length;
    this.update();
  }

  go(index) {
    this._currentIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    this.update();
  }

  update() {
    if (!this.track || this.slides.length <= 1) return;

    const slideWidth = this.track.clientWidth;
    const offset = slideWidth * this._currentIndex;

    this.updateMobileMediaHeight();

    this.track.style.transform = `translateX(-${offset}px)`;

    this.updateActiveSlide();
    this.handleAccessibility();
    this.updatePagination();
    this.updateControlsColors();
  }

  updateMobileMediaHeight() {
    if (!this.section || this.hasStaticMobileMediaHeight) return;

    const slide = this.slides[this._currentIndex];
    const mobileMedia = slide?.querySelector(".hero-slideshow-item-mobile");
    const height = mobileMedia?.getBoundingClientRect().height;

    if (height > 0) {
      this.section.style.setProperty("--media-height-mobile", `${height}px`);
    }
  }

  updateActiveSlide() {
    const activeIndexChanged = this._activeIndex !== this._currentIndex;

    this.slides.forEach((slide, index) => {
      if (index === this._currentIndex) {
        if (activeIndexChanged) {
          slide.classList.remove("active");
          void slide.offsetWidth;
        }

        slide.classList.add("active");
      } else {
        slide.classList.remove("active");
      }
    });

    this._activeIndex = this._currentIndex;
  }

  updateControlsColors() {
    if (!this.controlsPagination) return;

    const slide = this.slides[this._currentIndex];
    if (!slide) return;

    const item = slide.querySelector('.hero-slideshow-item');
    if (!item) return;

    const styles = getComputedStyle(item);
    const bg = styles.getPropertyValue('--content-background-color').trim();
    const text = styles.getPropertyValue('--content-text-color').trim();

    if (bg) this.controlsPagination.style.setProperty('--color-background', bg);
    if (text) this.controlsPagination.style.setProperty('--color-text', text);
  }

  handleControls() {
    this.controls.forEach((btn) => {
      btn.addEventListener("click", () => {
        if (btn.name === "previous") this.prev();
        if (btn.name === "next") this.next();

        this.stopAutoplay?.();
      });
    });
  }

  handlePagination() {
    this.paginationDots.forEach((dot, index) => {
      dot.addEventListener("click", () => {
        this.go(index);
        this.stopAutoplay?.();
      });
    });
  }

  updatePagination() {
    this.paginationDots.forEach((dot, index) => {
      if (index === this._currentIndex) {
        dot.classList.remove("active");
        void dot.offsetWidth; // force reflow to restart CSS animation
        dot.classList.add("active");
      } else {
        dot.classList.remove("active");
      }
    });

    if (this.paginationFraction) {
      const span = this.paginationFraction.querySelector("span");
      if (span) span.textContent = `${this._currentIndex + 1} / ${this.slides.length}`;
    }
  }

  handleAccessibility() {
    this.slides.forEach((slide, index) => {
      if (index === this._currentIndex) {
        slide.removeAttribute("aria-hidden");
        slide.removeAttribute("tabindex");

        slide.querySelectorAll("a, button").forEach((el) => {
          el.removeAttribute("tabindex");
        });
      } else {
        slide.setAttribute("aria-hidden", "true");
        slide.setAttribute("tabindex", "-1");

        slide.querySelectorAll("a, button").forEach((el) => {
          el.setAttribute("tabindex", "-1");
        });
      }
    });
  }

  handleKeyboard() {
    this.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") { e.preventDefault(); this.prev(); }
      if (e.key === "ArrowRight") { e.preventDefault(); this.next(); }
      if (e.key === "Home") this.go(0);
      if (e.key === "End") this.go(this.slides.length - 1);
    });
  }

  handleTouch() {
    let startX = null;

    this.addEventListener("touchstart", (e) => {
      if (e.touches.length > 1) return;
      startX = e.touches[0].clientX;
    });

    this.addEventListener("touchend", (e) => {
      if (startX === null || e.changedTouches.length > 1) return;

      const endX = e.changedTouches[0].clientX;
      const swipeDistance = startX - endX;

      if (swipeDistance > 10) this.next();
      if (swipeDistance < -10) this.prev();

      startX = null;

      if (this.dataset.autoplayStopped !== "true") this.startAutoplay?.();
    });
  }

  onBlockSelect(event) {
    if (!event.target.closest(`[data-section-id="${this.dataset.sectionId}"]`)) return;

    const blockIndex = event.target.dataset.index;
    if (blockIndex !== undefined) this.go(Number(blockIndex));

    this.stopAutoplay?.();
  }

  initAutoplay() {
    const autoplayInterval = this.dataset.autoplayInterval;
    if (!autoplayInterval || autoplayInterval === "0") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    this.startAutoplay = () => {
      clearInterval(this._intervalId);

      this._intervalId = setInterval(
        () => this.next(),
        Number(autoplayInterval) * 1000
      );

      this.dataset.autoplayStopped = "false";
      this.track.setAttribute("aria-live", "off");
    };

    this.stopAutoplay = () => {
      clearInterval(this._intervalId);
      this.dataset.autoplayStopped = "true";
      this.track.setAttribute("aria-live", "polite");
    };

    this.startAutoplay();

    this._autoplayEvents.forEach(([event, handler]) => {
      this.removeEventListener(event, handler);
    });

    this._autoplayEvents = [
      ["focusin", this.stopAutoplay],
      ["focusout", this.startAutoplay],
      ["mouseenter", this.stopAutoplay],
      ["mouseleave", this.startAutoplay],
    ];

    this._autoplayEvents.forEach(([event, handler]) => {
      this.addEventListener(event, handler);
    });
  }
}

if (!customElements.get("hero-slideshow-slider")) {
  customElements.define("hero-slideshow-slider", HeroSlideshowSlider);
}
